// miracles/main-theme-miracles/assets/miracles-quiz-ui.js
// Fase 3 orchestrator. Owns lifecycle, render loop, actions, processing timer,
// persistence, analytics stub, fallback. Screens are built by miracles-quiz-screens.js.

import { createQuiz, deserialize } from './miracles-quiz-engine.js';
import * as screens from './miracles-quiz-screens.js';
import * as persist from './miracles-quiz-persist.js';
import * as quizConfig from './miracles-quiz-config.js';
import { createCartAdapter } from './miracles-quiz-cart.js';

const PROCESSING_MIN_MS = 400;
const STATUS_TEXTS = [
  'Juntando tus respuestas…',
  'Mirando qué pasa con tus pestañas…',
  'Revisando lo que ya probaste…',
  'Listo. Ya lo tenemos.',
];
const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

class MiraclesQuiz extends HTMLElement {
  connectedCallback() {
    if (this._booted) return;
    this._booted = true;
    this.root = this.querySelector('[data-mq-root]');
    if (!this.root) return;

    const cfgEl = this.querySelector('[data-mq-config]');
    try {
      this.runtime = JSON.parse(cfgEl ? cfgEl.textContent : '{}');
    } catch (e) {
      this.runtime = {};
      this.root.replaceChildren(screens.el('p', { class: 'mq-body', text: 'No pudimos cargar el diagnóstico.' }));
      return;
    }
    const params = new URLSearchParams(location.search);
    this.debug = Boolean(this.runtime.debug || (window.Shopify && window.Shopify.designMode) || params.has('mq_debug'));
    this.reduce = Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    this.experimentArm = params.get('arm') || this.runtime.experimentArm || null;

    this._selection = null;
    this._lastScreen = null;
    this._procToken = 0;
    this._procTimers = [];
    this._pendingResume = null;

    const saved = persist.load();
    if (saved && saved.state && !['INTRO', 'RESULT'].includes(saved.state.currentScreen)) {
      const st = deserialize(saved);
      if (st) this._pendingResume = st;
    }
    this._quiz = createQuiz(quizConfig);
    this._cart = createCartAdapter(this.runtime);
    this._adding = false;
    this.track('quiz_view', this.base());
    this.render();
  }

  disconnectedCallback() {
    this._cancelProcessing();
    this._quiz = null;
    this._pendingResume = null;
    this._booted = false;
  }

  // ---- analytics stub (Fase 7 replaces the body) ----
  base() {
    const s = this._quiz ? this._quiz.getState() : {};
    return {
      event_id: (globalThis.crypto && crypto.randomUUID) ? crypto.randomUUID() : String(Date.now() + Math.random()),
      quiz_version: '1.6',
      quiz_session_id: s.sessionId || null,
      timestamp: Date.now(),
      experiment_arm: this.experimentArm,
    };
  }
  track(name, payload) {
    if (typeof globalThis.__mqTrack === 'function') { try { globalThis.__mqTrack(name, payload); } catch (e) {} }
    if (this.debug) console.debug('[mq]', name, payload);
    // Sink 1: DOM CustomEvent — GTM / theme scripts / a dataLayer bridge can listen.
    try { document.dispatchEvent(new CustomEvent('miracles:quiz', { detail: { event: name, ...payload } })); } catch (e) {}
    // Sink 2: Shopify Customer Events browser API — a custom Web Pixel in Admin
    // subscribes to `miracles_quiz_*` and forwards to GA4 / TikTok / Meta. The theme
    // does NOT load those pixels itself, so this cannot duplicate page_view/purchase.
    try {
      const s = window.Shopify;
      if (s && s.analytics && typeof s.analytics.publish === 'function') s.analytics.publish('miracles_quiz_' + name, payload);
    } catch (e) {}
  }

  screenName() {
    if (this._pendingResume) return 'RESUME';
    return this._quiz.getCurrentScreen();
  }

  ctx(extra) {
    return {
      quiz: this._quiz, runtime: this.runtime, debug: this.debug, reduce: this.reduce,
      selection: this._selection, model: null,
      on: (a, p) => this.on(a, p),
      ...extra,
    };
  }

  render() {
    const screen = this.screenName();
    const isTransition = screen !== this._lastScreen;
    const f = document.createDocumentFragment();
    const scr = screens.el('div', { class: 'mq-screen', dataset: { mqScreen: screen } });

    if (!['INTRO', 'RESUME'].includes(screen)) scr.appendChild(screens.topbar(this.ctx(), screen));

    let body;
    if (screen === 'RESUME') body = screens.resume(this.ctx());
    else if (screen === 'INTRO') body = screens.intro(this.ctx());
    else if (screen === 'Q1' || screen === 'Q2' || screen === 'Q4') body = screens.question(this.ctx(), screen);
    else if (screen === 'MICRO') body = screens.micro(this.ctx());
    else if (screen === 'Q3') body = screens.q3(this.ctx());
    else if (screen === 'PROCESSING') body = screens.processing(this.ctx());
    else if (screen === 'RESULT') body = this._resultBody();
    else body = screens.el('div');
    scr.appendChild(body);
    f.appendChild(scr);

    if (this.debug) {
      f.appendChild(screens.el('details', { class: 'mq-debug' }, [
        screens.el('summary', { text: 'debug' }),
        screens.el('pre', { text: JSON.stringify({ screen, state: this._quiz.getState() }, null, 2) }),
      ]));
    }

    this.root.replaceChildren(f);
    this._lastScreen = screen;

    if (isTransition) {
      const h = this.root.querySelector('h1') || this.root.querySelector('.mq-hero');
      if (h && h.focus) h.focus();
      if (this.scrollIntoView) this.scrollIntoView({ block: 'start', behavior: 'auto' });

      if (screen === 'MICRO') this.track('quiz_education_view', this.base());
      if (screen === 'PROCESSING') { this.track('quiz_processing_view', this.base()); this._startProcessing(); }
      if (screen === 'RESULT') {
        this._wireProof();
        if (this.runtime.products && this.runtime.products.recommended) this.track('offer_view', this.resultProps());
        this.track('diagnosis_view', this.resultProps());
        this.track('proof_view', this.resultProps());
        this.track('mechanism_view', this.resultProps());
        this.track('recommendation_view', this.resultProps());
      }
    }
  }

  _resultBody() {
    const m = this._quiz.buildResultModel();
    if (!m || m.ok !== true) {
      if (m && m.reason === 'BAD_STATE') { persist.clear(); this._quiz = createQuiz(quizConfig); return screens.intro(this.ctx()); }
      this._quiz.goBack();
      return screens.question(this.ctx(), 'Q4');
    }
    const tried = this._quiz.deriveTriedMethods();
    if (tried.includes('heat')) {
      const d = quizConfig.DIAGNOSIS_COPY[m.diagnosisKey];
      if (d && d.attemptsBody_heatKnown) m.attempts._heatKnownBody = d.attemptsBody_heatKnown;
    }
    const c = this.ctx({ model: m });
    return m.fallback ? screens.fallbackResult(c) : screens.result(c);
  }

  resultProps() {
    const s = this._quiz.getState();
    return {
      ...this.base(),
      current_solution: s.currentSolution,
      failure_mode: s.failureMode,
      tried_methods: this._quiz.deriveTriedMethods(),
      desired_improvement: s.desiredImprovement,
      recommended_product: 'recommended',
    };
  }

  // ---- processing timer ----
  _cancelProcessing() {
    this._procToken++;
    this._procTimers.forEach((t) => clearTimeout(t));
    this._procTimers = [];
  }
  _startProcessing() {
    this._cancelProcessing();
    const token = ++this._procToken;
    const proc = this.root.querySelector('.mq-proc');
    if (!proc) return;
    const rows = proc.querySelectorAll('.mq-proc-row');
    const meterI = proc.querySelector('.mq-proc-meter i');
    const status = proc.querySelector('.mq-proc-status');
    const done = proc.querySelector('.mq-primary');
    const skip = proc.querySelector('.mq-skip');
    const dur = this.reduce ? PROCESSING_MIN_MS : clamp(Number(this.runtime.ux && this.runtime.ux.processingDuration) || 2600, 1500, 4000);
    const alive = () => token === this._procToken && this.screenName() === 'PROCESSING';

    if (meterI) { meterI.style.transition = `transform ${dur}ms linear`; meterI.style.transform = 'scaleX(1)'; }

    if (this.reduce) {
      rows.forEach((r) => r.setAttribute('class', 'mq-proc-row is-in'));
      if (status) status.textContent = STATUS_TEXTS[STATUS_TEXTS.length - 1];
      if (done) done.removeAttribute('hidden');
      if (skip) skip.removeAttribute('hidden');
      return;
    }

    rows.forEach((r, i) => {
      const t = setTimeout(() => { if (alive()) r.setAttribute('class', 'mq-proc-row is-in'); }, 120 + i * 90);
      this._procTimers.push(t);
    });
    STATUS_TEXTS.forEach((txt, i) => {
      const t = setTimeout(() => { if (alive() && status) status.textContent = txt; }, Math.round((dur / STATUS_TEXTS.length) * i));
      this._procTimers.push(t);
    });
    this._procTimers.push(setTimeout(() => { if (alive() && skip) skip.removeAttribute('hidden'); }, 600));
    this._procTimers.push(setTimeout(() => {
      if (!alive()) return;
      if (status) status.textContent = STATUS_TEXTS[STATUS_TEXTS.length - 1];
      if (done) done.removeAttribute('hidden');
    }, dur));
  }

  _save() { try { persist.save(this._quiz.serialize()); } catch (e) {} }

  // Click-to-load the current diagnosis's proof video (§32/§33: no preload, no other route's media).
  _wireProof() {
    const media = this.root.querySelector('[data-mq-proof-video]');
    if (!media) return;
    const btn = media.querySelector('.mq-proof-play');
    if (!btn) return;
    btn.addEventListener('click', () => {
      const url = media.getAttribute('data-mq-proof-video');
      const poster = media.getAttribute('data-mq-proof-poster');
      const video = screens.el('video', {
        src: url, controls: true, playsinline: true, preload: 'none',
        poster: poster || null, class: 'mq-proof-video',
      });
      media.replaceChildren(video);
      if (video.play) { try { video.play(); } catch (e) {} }
      const fm = this._quiz.getState().failureMode;
      this.track('proof_play', { ...this.resultProps(), proof_key: fm });
    }, { once: true });
  }

  async _addToCart(key) {
    if (this._adding) return;
    const btn = this.root.querySelector(`[data-mq-add="${key}"]`);
    const errEl = this.root.querySelector('[data-mq-cart-error]');
    if (errEl) errEl.setAttribute('hidden', '');
    this._adding = true;
    if (btn) { btn._label = btn.textContent; btn.disabled = true; btn.textContent = quizConfig.OFFER_COPY.ctaAdding; }
    let r;
    try { r = await this._cart.add(key); } catch (e) { r = { ok: false, error: 'NETWORK' }; }
    if (r && r.ok) {
      const p = (this.runtime.products || {})[key] || {};
      this.track('quiz_add_to_cart', { ...this.resultProps(), product_key: key, variant_id: p.variantId, value: p.price });
      window.location.href = '/checkout';
      return;
    }
    if (btn) { btn.disabled = false; btn.textContent = btn._label; }
    this._adding = false;
    if (errEl) errEl.removeAttribute('hidden');
    if (this.debug) console.error('[mq] add-to-cart', key, r && r.error);
  }

  // ---- action dispatch ----
  on(action, payload) {
    try {
      switch (action) {
        case 'start':
          this._quiz.startQuiz(); this._selection = null; this._save();
          this.track('quiz_start', this.base());
          break;
        case 'select':
          this._selection = payload; this.render(); return;
        case 'toggleQ3':
          this._quiz.toggleQ3(payload); this._save(); this.render(); return;
        case 'addToCart':
          this._addToCart(payload);
          return;
        case 'back':
          this._cancelProcessing();
          this._quiz.goBack();
          this._selection = this._selectionForScreen();
          this._save();
          break;
        case 'continue': {
          const s = this._quiz.getCurrentScreen();
          if (s === 'Q1') {
            this._quiz.answerQ1(this._selection);
            this.track('quiz_q1_answer', { ...this.base(), current_solution: this._quiz.getState().currentSolution });
          } else if (s === 'Q2') {
            this._quiz.answerQ2(this._selection);
            const st = this._quiz.getState();
            this.track('quiz_q2_answer', { ...this.base(), current_solution: st.currentSolution, q2_answer_id: st.q2AnswerId, failure_mode: st.failureMode });
          } else if (s === 'MICRO') {
            this._quiz.continueFromMicro();
          } else if (s === 'Q3') {
            this._quiz.continueFromQ3();
            this.track('quiz_q3_answer', { ...this.base(), known_methods: this._quiz.deriveKnownMethods(), selected_methods: this._quiz.getState().selectedQ3Methods, tried_methods: this._quiz.deriveTriedMethods() });
          } else if (s === 'Q4') {
            this._quiz.answerQ4(this._selection);
            this.track('quiz_q4_answer', { ...this.base(), desired_improvement: this._quiz.getState().desiredImprovement });
          }
          this._selection = this._selectionForScreen();
          this._save();
          break;
        }
        case 'finishProcessing':
        case 'skipProcessing':
          this._cancelProcessing();
          this._quiz.finishProcessing();
          this._save();
          this.track('quiz_complete', this.resultProps());
          break;
        case 'resume':
          this._quiz = createQuiz(quizConfig, { initialState: this._pendingResume });
          this._pendingResume = null;
          this._selection = this._selectionForScreen();
          break;
        case 'restart':
          persist.clear();
          this._quiz = createQuiz(quizConfig);
          this._pendingResume = null;
          this._selection = null;
          break;
        default:
          return;
      }
    } catch (e) {
      if (this.debug) console.error('[mq]', action, e);
    }
    this.render();
  }

  _selectionForScreen() {
    if (this._pendingResume) return null;
    const s = this._quiz.getCurrentScreen();
    const st = this._quiz.getState();
    if (s === 'Q1') return st.currentSolution ? this._q1IdFor(st.currentSolution) : null;
    if (s === 'Q2') return st.q2AnswerId || null;
    if (s === 'Q4') return st.desiredImprovement || null;
    return null;
  }
  _q1IdFor(value) {
    const o = quizConfig.Q1.options.find((x) => x.value === value);
    return o ? o.id : null;
  }
}

customElements.define('miracles-quiz', MiraclesQuiz);
